/**
 * Authentication Service for 123bimlive.com
 * This service handles checking and attaching Cognito authentication tokens
 * for requests to the 123bimlive.com domain
 */

import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { getIdToken, getAccessToken } from '../utils/authSession';

class AuthService {
  /**
   * Check if user is currently authenticated via Cognito
   * @returns {Promise<boolean>} True if authenticated, false otherwise
   */
  async isAuthenticated() {
    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      console.log('User not authenticated:', error);
      return false;
    }
  }

  /**
   * Get the current user's Cognito ID token
   * @returns {Promise<string|null>} The ID token if authenticated, null otherwise
   */
  async getCognitoIdToken() {
    try {
      return await getIdToken();
    } catch (error) {
      console.log('Could not get Cognito ID token:', error);
      return null;
    }
  }

  /**
   * Get the current user's Cognito access token
   * @returns {Promise<string|null>} The access token if authenticated, null otherwise
   */
  async getCognitoAccessToken() {
    try {
      return await getAccessToken();
    } catch (error) {
      console.log('Could not get Cognito access token:', error);
      return null;
    }
  }

  /**
   * Check if a URL belongs to the 123bimlive.com domain
   * @param {string} url - The URL to check
   * @returns {boolean} True if URL is from 123bimlive.com domain
   */
  isBimLiveUrl(url) {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname === '123bimlive.com' || urlObj.hostname === 'www.123bimlive.com';
    } catch (error) {
      console.log('Invalid URL:', url);
      return false;
    }
  }

  /**
   * Attach authentication token to a URL as a query parameter
   * Used for passing token to 123bimlive.com for validation
   * @param {string} url - The base URL
   * @param {string} token - The authentication token
   * @returns {string} The URL with token attached
   */
  attachTokenToUrl(url, token) {
    if (!url || !token) return url;
    
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('authToken', token);
      return urlObj.toString();
    } catch (error) {
      console.log('Error attaching token to URL:', error);
      return url;
    }
  }

  /**
   * Verify token with backend server
   * @returns {Promise<boolean>} True if token is valid
   */
  async verifyTokenWithBackend() {
    try {
      const token = await this.getCognitoIdToken();
      if (!token) {
        return false;
      }

      // Import axios (or use fetch)
      const axios = (await import('axios')).default;
      const { API_BASE_URL } = await import('../API/api');
      
      const response = await axios.post(`${API_BASE_URL}/api/verify-token`, {
        token: token
      });

      return response.data && response.data.valid === true;
    } catch (error) {
      console.error('Backend token verification failed:', error);
      return false;
    }
  }

  /**
   * Get the authenticated URL for 123bimlive.com
   * With cookie-based authentication, we don't need to modify the URL.
   * The cognito_token cookie is automatically sent with requests to the same domain.
   * @param {string} url - The original URL
   * @returns {Promise<string>} The URL unchanged (cookies handle authentication)
   */
  async getAuthenticatedUrl(url) {
    // With cookie-based authentication, we don't need to modify URLs
    // The cognito_token cookie is set by FileContext.jsx and automatically
    // sent with requests to 123bimlive.com domain
    console.log('🔐 Using cookie-based authentication - URL unchanged:', url);
    return url;
  }

  /**
   * Get token information from localStorage (for debugging)
   * @returns {Object|null} Token information
   */
  async getTokenInfo() {
    try {
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken) {
        return null;
      }
      const idToken = tokens.idToken.toString();
      const accessToken = tokens.accessToken?.toString();
      const idTokenPayload = tokens.idToken.payload;

      return {
        authenticated: true,
        idToken: idToken,
        accessToken: accessToken,
        email: idTokenPayload.email,
        groups: idTokenPayload['cognito:groups'] || [],
        expiresAt: new Date(idTokenPayload.exp * 1000),
        isExpired: Date.now() > idTokenPayload.exp * 1000
      };
    } catch (error) {
      console.log('Could not get token info:', error);
      return null;
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;

// Make available globally for debugging
window.authService = authService;

